import { twMerge } from "tailwind-merge";
import { createContext, useContext } from "react";

const tableContext = createContext({
  dark: false,
  bordered: false,
  hover: false,
  striped: false,
  sm: false,
});

function Table({ className, dark, bordered, hover, striped, sm, ...props }) {
  return (
    <tableContext.Provider
      value={{
        dark: dark,
        bordered: bordered,
        hover: hover,
        striped: striped,
        sm: sm,
      }}
    >
      <table
        className={twMerge([
          "w-full text-left",
          dark && "bg-dark text-white dark:bg-black/30",
          className,
        ])}
        {...props}
      >
        {props.children}
      </table>
    </tableContext.Provider>
  );
}

const theadContext = createContext({
  variant: "default",
});

function TableThead({ className, variant = "default", ...props }) {
  return (
    <theadContext.Provider value={{ variant }}>
      <thead
        className={twMerge(
          variant === "light" && "bg-slate-200/60 dark:bg-slate-200",
          variant === "dark" && "bg-dark text-white dark:bg-black/30",
          "bg-[#F8FAFB]",
          className
        )}
        {...props}
      >
        {props.children}
      </thead>
    </theadContext.Provider>
  );
}

function TableTbody({ className, ...props }) {
  return (
    <tbody className={className} {...props}>
      {props.children}
    </tbody>
  );
}

function TableTr({ className, ...props }) {
  const table = useContext(tableContext);
  return (
    <tr
      className={twMerge([
        table.hover &&
        "[&:hover_td]:bg-slate-100 [&:hover_td]:dark:bg-darkmode-300 [&:hover_td]:dark:bg-opacity-50 ",
        table.striped &&
        "[&:nth-of-type(odd)_td]:bg-slate-100 [&:nth-of-type(odd)_td]:dark:bg-darkmode-300 [&:nth-of-type(odd)_td]:dark:bg-opacity-50",
        className,
      ])}
      {...props}
    >
      {props.children}
    </tr>
  );
}

function TableTh({ className, ...props }) {
  const table = useContext(tableContext);
  const thead = useContext(theadContext);
  return (
    <th
      className={twMerge([
        "font-medium p-2 px-8 border-b-2 dark:border-darkmode-300",
        thead.variant === "light" && "border-b-0 text-slate-700",
        thead.variant === "dark" && "border-b-0",
        table.dark && "border-slate-600 dark:border-darkmode-300",
        table.bordered && "border-l border-r border-t",
        table.sm && "px-4 py-2",
        className,
      ])}
      {...props}
    >
      {props.children}
    </th>
  );
}

function TableTd({ className, ...props }) {
  const table = useContext(tableContext);
  return (
    <td
      className={twMerge([
        "p-2.5 px-8 dark:border-darkmode-300 border-b border-[#EBEBEB]",
        table.dark && "border-slate-600 dark:border-darkmode-300",
        table.bordered && "border-l border-r border-t",
        table.sm && "px-4 py-2",
        className,
      ])}
      {...props}
    >
      {props.children}
    </td>
  );
}

// Assign the components as properties of the Table object
Table.Thead = TableThead;
Table.Tbody = TableTbody;
Table.Tr = TableTr;
Table.Th = TableTh;
Table.Td = TableTd;

export default Table;
